import * as React from 'react';

import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import NotificationItem from '../../components/Notifications/NotificationItem';
import { useVenueListNotificationsQuery } from '../../hooks/reactQuery/notifications/useVenueListNotificationsQuery';
import useVenueId from '../../hooks/useVenueId';

const Notifications: React.FC = () => {
  const venueID = useVenueId();
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useVenueListNotificationsQuery(venueID);

  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetching) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetching, fetchNextPage]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!data.length) {
      return (
        <Stack alignItems='center' spacing={1.5} sx={{ py: 10, color: 'rgba(0, 0, 0, 0.45)' }}>
          <NotificationsNoneIcon sx={{ fontSize: 56 }} />
          <Typography variant='body1'>You have no notifications yet.</Typography>
          <Typography variant='body2' sx={{ color: 'rgba(0, 0, 0, 0.4)' }}>
            New activity from your members will show up here.
          </Typography>
        </Stack>
      );
    }

    return (
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          border: '1px solid rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
        }}
      >
        {data.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} />
        ))}

        <Box ref={sentinelRef} sx={{ height: 1 }} />

        {hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetching}
              size='small'
              sx={{ color: '#765BC6' }}
            >
              {isFetching ? <CircularProgress size={18} /> : 'Load more'}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, p: 4 }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Typography variant='h5' fontWeight='bold' sx={{ textAlign: 'center' }}>
          Notifications
        </Typography>
        <Box mt={3}>{renderBody()}</Box>
      </Box>
    </Box>
  );
};

export default Notifications;
